import { NavLink } from 'react-router';
import { PRIMARY_NAV, type NavDestination } from './navigation';
import { cn } from '@/lib/cn';

/**
 * The bottom bar, for phones.
 *
 * A 15rem left rail on a 390px screen leaves 6rem for the film, so below the
 * medium breakpoint the sidebar is replaced by this rather than squeezed.
 *
 * It sits at the bottom because that is where a thumb is, and it carries the
 * home indicator inset itself so the last row of buttons is not underneath it.
 */
export function MobileNav() {
  return (
    <nav
      aria-label="Main"
      className={cn(
        'flex shrink-0 items-stretch justify-around gap-1 border-t border-edge bg-bg-sunken/95 px-1 pt-1 md:hidden',
        'backdrop-blur-xl',
      )}
      style={{ paddingBottom: 'max(0.25rem, var(--safe-bottom))' }}
    >
      {PRIMARY_NAV.filter((item) => item.status === 'ready').map((item) => (
        <MobileNavItem key={item.to} item={item} />
      ))}
    </nav>
  );
}

function MobileNavItem({ item }: { readonly item: NavDestination }) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          // 3.25rem is above the 44px Apple asks for as a minimum target, and
          // the label is what makes an unfamiliar icon usable.
          'flex min-h-[3.25rem] flex-1 flex-col items-center justify-center gap-1 rounded-[var(--radius-md)] px-1 py-1.5',
          isActive ? 'text-accent' : 'text-text-3',
        )
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={21} strokeWidth={isActive ? 2.4 : 2} aria-hidden="true" />
          <span className="text-[0.625rem] font-medium leading-none">{shortLabel(item.label)}</span>
        </>
      )}
    </NavLink>
  );
}

/**
 * Names that fit a fifth of a phone screen.
 *
 * "Watch Together" wraps to three lines at this width and pushes the bar to
 * twice its height, so the long names are shortened here rather than being
 * truncated into something unreadable.
 */
function shortLabel(label: string): string {
  switch (label) {
    case 'Watch Together':
      return 'Together';
    case 'Private Viewing':
      return 'Private';
    case 'Find a film':
      return 'Find';
    default:
      return label;
  }
}

export { shortLabel };
